"use client";

/**
 * P2-04 / P5 / P18-19 · The card's two decorations, in one island module.
 *
 * `<CardStatusBadge>` is the single status badge (P2-DB04 r2 §02 precedence:
 * out-of-stock › sale › low-stock › new). `<CardRating>` is the social-proof
 * line from the published-only review denorm.
 *
 * Both are client components for the same small reason — one translates two of
 * its four readings, the other asks a brand flag and renders `<StarRating>`,
 * which is itself a client component — and both are handed values the pure
 * helpers (`deriveCardStatus`, `discountPercent`) have already decided, so
 * neither needs the card. Keeping them in one module keeps the card's
 * client-reference count (and so the route's bundle) down.
 */
import { useOptionalFeatureFlag } from "~/components/feature-flags/provider";
import { StarRating } from "~/components/storefront/reviews/star-rating";
import { Badge } from "~/components/ui/badge";
import { useTranslator } from "~/i18n/provider";

import type { CardStatus } from "./derive";

export interface CardStatusBadgeProps {
  status: CardStatus;
  /** Pre-computed discount percentage, or null when there is no sale price. */
  discountPct?: number | null;
  /** Units left — only read for the `low-stock` reading. */
  totalStock?: number;
}

export function CardStatusBadge({
  status,
  discountPct = null,
  totalStock = 0,
}: CardStatusBadgeProps) {
  const t = useTranslator();
  if (status === null) return null;
  switch (status) {
    case "sold-out":
      return <Badge tone="neutral">{t("product.outOfStock")}</Badge>;
    case "sale":
      return (
        <Badge tone="danger">
          {discountPct !== null ? `−${discountPct}%` : t("product.sale")}
        </Badge>
      );
    case "low-stock":
      return <Badge tone="warning">Only {totalStock} left</Badge>;
    case "new":
      return <Badge tone="primary">New</Badge>;
  }
}

export interface CardRatingProps {
  ratingAverage: number | null | undefined;
  ratingCount: number | undefined;
}

export function CardRating({ ratingAverage, ratingCount }: CardRatingProps) {
  const enabled = useOptionalFeatureFlag("reviews");
  // Loose checks on purpose: pre-P5 cached payloads carry neither field.
  if (!enabled || !ratingCount || ratingAverage == null) return null;
  return (
    <span className="mt-1 flex items-center gap-1.5">
      <StarRating value={ratingAverage} size="sm" />
      <span className="text-[11px] tabular-nums text-fg-muted">
        ({ratingCount})
      </span>
    </span>
  );
}
